/**
 * Tiny typed pub/sub for copilot effects. Bridges subscribe by effect type;
 * the provider emits frames as they arrive over the WS. Decoupling via this
 * emitter keeps other features from importing copilot internals — they only
 * depend on the effect contract.
 */
import type { CopilotEffect, CopilotEffectType, EffectPayloads } from './types';

type Handler<T extends CopilotEffectType> = (
  payload: EffectPayloads[T]
) => void;

const handlers = new Map<CopilotEffectType, Set<Handler<CopilotEffectType>>>();

export function onEffect<T extends CopilotEffectType>(
  type: T,
  handler: Handler<T>
): () => void {
  const set = handlers.get(type) ?? new Set();
  set.add(handler as Handler<CopilotEffectType>);
  handlers.set(type, set);
  return () => {
    set.delete(handler as Handler<CopilotEffectType>);
  };
}

export function emitEffect(effect: CopilotEffect): void {
  const set = handlers.get(effect.type);
  if (!set) return;
  set.forEach((handler) => {
    try {
      handler(effect.payload);
    } catch (err) {
      // A misbehaving bridge must not break sibling bridges or the WS loop.
      // eslint-disable-next-line no-console
      console.error('[copilot] effect handler failed', effect.type, err);
    }
  });
}

/** Test helper: drop all subscriptions. */
export function resetEffectsForTest(): void {
  handlers.clear();
}
